import * as React from 'react';

import { emptyObject } from 'bones/constants/empty';
import { STARDOG_LABEL_IRI } from 'bones/constants/iris';
import { MimeType } from 'bones/constants/mimeTypes';
import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import { VoiceboxPanelDataContextValue } from 'bones/constants/voicebox/panel';
import { VoiceboxSettingsContextValue } from 'bones/constants/voicebox/settings';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';
import { useWithLoading } from 'bones/utils/hooks/useWithLoading';
import { makeStardogQueryName } from 'bones/utils/voicebox/stardogQuery';
import {
  QueryArgs,
  ResultBindings,
  usePortalVoiceboxStardogQuery,
} from 'bones/utils/voicebox/usePortalVoiceboxStardogQuery';

const getLabelsQueryArgs = (
  missingIris: string[],
  querySchema: boolean
): QueryArgs<
  ResultBindings<{ subject: string; label: string }>,
  Record<string, string>
> => ({
  query: `\
# ${makeStardogQueryName('Voicebox Labels', ...missingIris)}
SELECT DISTINCT
  ?subject
  ?label
WHERE {
  VALUES ?subject { ${missingIris.map((iri) => `<${iri}>`).join(' ')} }
  ?subject <${STARDOG_LABEL_IRI}> ?label
}
`,
  queryResultsMime: MimeType.JSON_SPARQL_RESULTS,
  querySchema,
  parseBody: async (body) => {
    const bindings = body?.results?.bindings || [];
    return bindings.reduce<Record<string, string>>((acc, binding) => {
      if (binding.subject?.value && binding.label?.value) {
        acc[binding.subject.value] = binding.label.value;
      }

      return acc;
    }, {});
  },
});

export const usePortalVoiceboxLabelsFetcher = (
  connectionIdx: number,
  settingsContext: VoiceboxSettingsContextValue,
  labelsByIri: Record<string, string>,
  setLabels: (
    labels: React.SetStateAction<VoiceboxPanelDataContextValue['labels']>
  ) => void
) => {
  const { modelsByDatabaseId, settings } = settingsContext;
  const modelsById = modelsByDatabaseId[settings.databaseId] || emptyObject;
  const { classesByIri } = modelsById[settings.model || DEFAULT_MODEL_NAME] || {
    classesByIri: emptyObject,
  };

  const isMounted = useIsMounted();
  const withLoading = useWithLoading('usePortalVoiceboxLabelsFetcher');

  const fetchLabels = usePortalVoiceboxStardogQuery(
    connectionIdx,
    settingsContext,
    getLabelsQueryArgs
  );

  return React.useCallback(
    async (iris: string[]) => {
      const missingModelConceptIris: string[] = [];
      const missingDataIris: string[] = [];
      iris.forEach((iri) => {
        if (!labelsByIri[iri]) {
          if (classesByIri[iri]) {
            missingModelConceptIris.push(iri);
          } else {
            missingDataIris.push(iri);
          }
        }
      });
      if (!missingModelConceptIris.length && !missingDataIris.length) {
        return;
      }

      await withLoading(
        (loading) => setLabels((prev) => ({ ...prev, loading })),
        () =>
          Promise.all(
            (
              [
                [true, missingModelConceptIris],
                [false, missingDataIris],
              ] as const
            ).map(async ([querySchema, missingIris]) => {
              if (!missingIris.length) {
                return;
              }

              const newLabelsByIri = await fetchLabels(
                missingIris,
                querySchema
              );
              if (
                !newLabelsByIri ||
                !Object.keys(newLabelsByIri).length ||
                !isMounted.current
              ) {
                return;
              }

              setLabels((prev) => ({
                ...prev,
                labelsByIri: {
                  ...prev.labelsByIri,
                  ...newLabelsByIri,
                },
              }));
            })
          )
      );
    },
    [classesByIri, fetchLabels, isMounted, labelsByIri, setLabels, withLoading]
  );
};
